import { useState } from "react";
import { Phone, MessageCircle, Calendar, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface LPStickyFooterCTAProps {
  onBookVisit?: () => void;
  projectName?: string;
}

const LPStickyFooterCTA = ({ onBookVisit, projectName = "GPR Infra plots" }: LPStickyFooterCTAProps) => {
  const [dialogType, setDialogType] = useState<"call" | "whatsapp" | null>(null);
  const [formData, setFormData] = useState({ name: "", phone: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const whatsappUrl = "https://wa.me/918297978080?text=" + encodeURIComponent(
    `Hi, I am interested in ${projectName}.\nPlease share price, layout & site visit details.`
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanPhone = formData.phone.replace(/\D/g, '');
    
    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!/^\d{10}$/.test(cleanPhone)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    setIsSubmitting(true);
    try {
      await supabase.from("leads").insert({
        name: formData.name.trim(),
        phone: cleanPhone,
        email: `${cleanPhone}@${dialogType}.gprinfra.com`,
        source: dialogType === "call" ? "Call Button Click" : "WhatsApp Button Click",
        interest_product: projectName,
        status: "new",
        priority: "hot",
        notes: `User clicked ${dialogType} button for ${projectName}`
      });

      // Send notification email
      try {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-form-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'quick-inquiry',
            name: formData.name.trim(),
            phone: cleanPhone,
            project: projectName
          })
        });
      } catch (emailError) {
        console.error("Email notification error:", emailError);
      }

      setIsSuccess(true);
      toast.success("✅ Your details saved! Connecting you now...");
      setFormData({ name: "", phone: "" });
    } catch (error) {
      console.error("Lead capture error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAction = () => {
    if (dialogType === "call") {
      window.location.href = "tel:8297978080";
    } else {
      window.open(whatsappUrl, "_blank");
    }
    closeDialog();
  };

  const closeDialog = () => {
    setDialogType(null);
    setIsSuccess(false);
  };

  return (
    <>
      {/* Mobile Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-card/95 backdrop-blur-lg border-t-2 border-primary/20 shadow-2xl safe-area-inset-bottom">
        <div className="p-2 pb-3">
          <div className="grid grid-cols-3 gap-2">
            <Button
              className="w-full flex-col gap-0.5 h-14 bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 rounded-xl shadow-lg text-primary-foreground"
              onClick={() => setDialogType("call")}
            >
              <Phone className="h-5 w-5" />
              <span className="text-[10px] font-semibold">Call Now</span>
            </Button>

            <Button
              className="flex-col gap-0.5 h-14 bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#22c55e] hover:to-[#15803d] rounded-xl shadow-lg text-white"
              onClick={() => setDialogType("whatsapp")}
            >
              <MessageCircle className="h-5 w-5" />
              <span className="text-[10px] font-semibold">WhatsApp</span>
            </Button>
            <Button
              className="flex-col gap-0.5 h-14 bg-gradient-to-r from-accent to-warning hover:from-accent/90 hover:to-warning/90 rounded-xl shadow-lg text-accent-foreground"
              asChild
            >
              <a 
                href="#book-site-visit"
                onClick={(e) => {
                  e.preventDefault();
                  onBookVisit?.();
                  const formElement = document.getElementById('book-site-visit');
                  if (formElement) {
                    formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                <Calendar className="h-5 w-5" />
                <span className="text-[10px] font-semibold">Site Visit</span>
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Desktop Side Buttons */}
      <div className="hidden md:flex fixed right-4 top-1/2 -translate-y-1/2 z-50 flex-col gap-3">
        <Button
          className="flex-col gap-1 h-20 w-20 bg-gradient-to-br from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 rounded-2xl shadow-2xl text-primary-foreground border-2 border-primary-foreground/20 cursor-pointer"
          onClick={() => setDialogType("call")}
        >
          <Phone className="h-6 w-6" />
          <span className="text-[10px] font-bold">Call Now</span>
        </Button>

        <Button
          className="flex-col gap-1 h-20 w-20 bg-gradient-to-br from-[#25D366] to-[#128C7E] hover:from-[#22c55e] hover:to-[#15803d] rounded-2xl shadow-2xl text-white border-2 border-white/20"
          onClick={() => setDialogType("whatsapp")}
        >
          <MessageCircle className="h-6 w-6" />
          <span className="text-[10px] font-bold">WhatsApp</span>
        </Button>
        <Button
          className="flex-col gap-1 h-20 w-20 bg-gradient-to-br from-accent to-warning hover:from-accent/90 hover:to-warning/90 rounded-2xl shadow-2xl text-accent-foreground border-2 border-accent-foreground/20"
          onClick={() => {
            onBookVisit?.();
            const formElement = document.getElementById('book-site-visit');
            if (formElement) {
              formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
        >
          <Calendar className="h-6 w-6" />
          <span className="text-[10px] font-bold">Site Visit</span>
        </Button>
      </div>

      {/* Lead Capture Dialog */}
      <Dialog open={dialogType !== null} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="sm:max-w-md border-0 p-0 overflow-hidden bg-transparent shadow-none">
          <div className="relative bg-card rounded-2xl overflow-hidden shadow-2xl border border-border">
            {/* Header */}
            <div className={`p-5 text-center relative ${dialogType === "whatsapp" ? "bg-gradient-to-r from-[#25D366] to-[#128C7E]" : "bg-gradient-to-r from-primary to-secondary"}`}>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 text-white hover:bg-white/20 rounded-full"
                onClick={closeDialog}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </Button>
              
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/20 backdrop-blur mb-3">
                {dialogType === "whatsapp" ? (
                  <MessageCircle className="h-7 w-7 text-white" />
                ) : (
                  <Phone className="h-7 w-7 text-white" />
                )}
              </div>
              <h2 className="text-xl font-bold text-white">
                {isSuccess ? "Thank You!" : dialogType === "whatsapp" ? "Connect on WhatsApp" : "Quick Connect"}
              </h2>
              <p className="text-white/80 text-sm mt-1">
                {isSuccess 
                  ? "Your details saved successfully" 
                  : "Enter details so we can serve you better"}
              </p>
            </div>

            {/* Content */}
            <div className="p-5">
              {isSuccess ? (
                <div className="text-center py-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/20 mb-4">
                    <CheckCircle2 className="h-8 w-8 text-success" />
                  </div>
                  <p className="text-foreground font-medium mb-4">
                    {dialogType === "whatsapp" ? "Click below to chat on WhatsApp:" : "Click below to call us:"}
                  </p>
                  <Button 
                    className={`w-full h-14 text-lg font-bold rounded-xl gap-2 ${dialogType === "whatsapp" ? "bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#22c55e] hover:to-[#15803d] text-white" : "btn-gradient"}`}
                    onClick={handleAction}
                  >
                    {dialogType === "whatsapp" ? (
                      <>
                        <MessageCircle className="h-5 w-5" />
                        Open WhatsApp
                      </>
                    ) : (
                      <>
                        <Phone className="h-5 w-5" />
                        Call 8297978080
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-muted-foreground mt-3">
                    We'll also call you back if needed
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="text"
                    placeholder="Your Name *"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-12 rounded-xl border-2 focus:border-primary text-base"
                    required
                  />
                  <Input
                    type="tel"
                    placeholder="Your Phone Number *"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="h-12 rounded-xl border-2 focus:border-primary text-base"
                    pattern="[0-9]{10}"
                    required
                  />
                  
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full h-14 text-lg font-bold rounded-xl ${dialogType === "whatsapp" ? "bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#22c55e] hover:to-[#15803d] text-white" : "btn-gradient"}`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Saving...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        {dialogType === "whatsapp" ? (
                          <>
                            <MessageCircle className="h-5 w-5" />
                            Continue to WhatsApp
                          </>
                        ) : (
                          <>
                            <Phone className="h-5 w-5" />
                            Continue to Call
                          </>
                        )}
                      </span>
                    )}
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 bg-card text-muted-foreground">or skip</span>
                    </div>
                  </div>

                  <Button 
                    type="button"
                    variant="outline"
                    className="w-full h-12 rounded-xl border-2"
                    onClick={handleAction}
                  >
                    {dialogType === "whatsapp" ? (
                      <>
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Skip & Open WhatsApp
                      </>
                    ) : (
                      <>
                        <Phone className="h-4 w-4 mr-2" />
                        Skip & Call Directly
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    🔒 We respect your privacy. No spam.
                  </p>
                </form>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LPStickyFooterCTA;
